import classNames from 'classnames';
import { useRef } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import {
  DateData,
  DurationData,
  LocationChangeParameters,
  LocationData,
  PersonsData,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import SearchBarDateDropdown from '@/components/organisms/search-bar/shared/search-bar-date-dropdown/search-bar-date-dropdown';
import SearchBarDurationDropdown from '@/components/organisms/search-bar/shared/search-bar-duration-dropdown/search-bar-duration-dropdown';
import SearchBarLocationsDropdown from '@/components/organisms/search-bar/shared/search-bar-locations-dropdown/search-bar-locations-dropdown';
import SearchBarPersonsDropdown from '@/components/organisms/search-bar/shared/search-bar-persons-dropdown/search-bar-persons-dropdown';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './search-bar-widget-dropdown.module.scss';

export interface SearchBarWidgetDropdownProps {
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** An event that is triggered on change date */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on change duration */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on change location */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on change persons */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on change transportation */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back chevron */
  onClickBack?: () => void;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** An event that is triggered on clicking the search button */
  onClickSearch?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Section data for the dropdown */
  section?: SearchBarWrapperSectionData;
}

const SearchBarWidgetDropdown = ({
  className,
  customRangeStyle,
  onChangeDate,
  onChangeDuration,
  onChangeLocations,
  onChangePersons,
  onChangeTransportation,
  onClickBack,
  onClickClose,
  onClickNext,
  onClickSearch,
  section,
}: SearchBarWidgetDropdownProps) => {
  const { t } = useI18n();

  const searchPersonsRef = useRef<{ addRoom: () => void; clear: () => void }>(
    null,
  );

  const searchDateRef = useRef<{ addRoom: () => void; clear: () => void }>(
    null,
  );

  const searchLocationsRef = useRef<{ addRoom: () => void; clear: () => void }>(
    null,
  );

  const renderSections = () => {
    if (section === undefined) {
      return;
    }

    switch (section.type) {
      case SearchBarWrapperSectionType.DURATION:
        return (
          <SearchBarDurationDropdown
            className={classNames(styles.searchSection, styles.durationSection)}
            description={section.value ?? section.placeholder}
            isVisible
            isWidget
            label={section.label}
            onChange={onChangeDuration}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            selectOptions={
              (section.dropdownData as DurationData)?.durationOptions ?? []
            }
          />
        );

      case SearchBarWrapperSectionType.PERSONS:
        return (
          <SearchBarPersonsDropdown
            ref={searchPersonsRef}
            adultThreshold={
              (section.dropdownData as PersonsData)?.adultThreshold ?? 0
            }
            className={styles.searchSection}
            defaultAdultNumber={
              (section.dropdownData as PersonsData)?.defaultAdultNumber
            }
            description={section.value ?? section.placeholder}
            isVisible
            isWidget
            label={section.label}
            maxPersons={(section.dropdownData as PersonsData)?.maxPersons}
            maxRooms={(section.dropdownData as PersonsData)?.maxRooms}
            onChange={onChangePersons}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            roomDistributionWarning={
              (section.dropdownData as PersonsData)?.roomDistributionWarning
            }
            roomsState={(section.dropdownData as PersonsData)?.roomsState}
          />
        );

      case SearchBarWrapperSectionType.LOCATIONS:
        return (
          <SearchBarLocationsDropdown
            ref={searchLocationsRef}
            className={classNames(styles.searchSection, styles.locationSection)}
            description={section.value ?? section.placeholder}
            isVisible
            isWidget
            label={section.label}
            locations={(section.dropdownData as LocationData)?.locations ?? []}
            onChangeLocations={onChangeLocations}
            onChangeTransportation={onChangeTransportation}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            transportationOptions={
              (section.dropdownData as LocationData)?.transportationOptions ??
              []
            }
          />
        );

      case SearchBarWrapperSectionType.DATE:
        return (
          <SearchBarDateDropdown
            ref={searchDateRef}
            availableDates={
              (section.dropdownData as DateData)?.availableDates ?? []
            }
            className={styles.searchSection}
            customRangeStyle={customRangeStyle}
            description={section.value ?? section.placeholder}
            focusFirstSelectableMonth={
              (section.dropdownData as DateData)?.focusFirstSelectableMonth
            }
            isVisible
            isWidget
            label={section.label}
            maxDate={(section.dropdownData as DateData)?.maxDate as Date}
            maxRangeDays={(section.dropdownData as DateData)?.maxRangeDays}
            minDate={(section.dropdownData as DateData)?.minDate as Date}
            onChange={onChangeDate}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            rangeSelect={(section.dropdownData as DateData)?.rangeSelect}
            rangeSize={(section.dropdownData as DateData)?.rangeSize}
            selectedDate={(section.dropdownData as DateData)?.selectedDate}
            selectedMaxDate={
              (section.dropdownData as DateData)?.selectedMaxDate
            }
          />
        );

      default:
        return null;
    }
  };

  const handleAddRoom = () => {
    searchPersonsRef.current?.addRoom();
  };

  const getTotalAdultCount = (section: SearchBarWrapperSectionData): number => {
    const personsData = section.dropdownData as PersonsData;

    return (
      personsData?.roomsState?.rooms?.reduce((total, room) => {
        return total + (room.numberOfAdults ?? 0);
      }, 0) ?? 0
    );
  };

  const renderAddRoomButton = (section: SearchBarWrapperSectionData) => {
    if (section && section.type !== SearchBarWrapperSectionType.PERSONS) {
      return;
    }

    const personsData = section.dropdownData as PersonsData;

    if (personsData === undefined) return;

    const {
      roomsState,
      defaultAdultNumber = 0,
      maxRooms = 0,
      maxPersons = 0,
    } = personsData;

    const rooms = roomsState?.rooms ?? [];

    const automaticRoomAssignment =
      roomsState?.automaticRoomAssignment ?? false;

    return (
      !automaticRoomAssignment &&
      rooms.length < maxRooms &&
      getTotalAdultCount(section) + defaultAdultNumber <= maxPersons && (
        <Link className={styles.link} onClick={handleAddRoom}>
          <Typography tagStyle="bodySmall">
            {t('items.search.searchBar.persons.addExtraRoom')}
          </Typography>
        </Link>
      )
    );
  };

  const handleClickButton = () => {
    if (section?.type === SearchBarWrapperSectionType.DATE) {
      onClickSearch?.();
    } else {
      onClickNext?.();
    }
  };

  const handleClickClear = () => {
    if (section === undefined) {
      return;
    }

    switch (section.type) {
      case SearchBarWrapperSectionType.PERSONS:
        {
          searchPersonsRef.current?.clear();
        }
        break;

      case SearchBarWrapperSectionType.LOCATIONS:
        {
          searchLocationsRef.current?.clear();
        }
        break;

      case SearchBarWrapperSectionType.DATE:
        {
          searchDateRef.current?.clear();
        }
        break;

      default:
        return;
    }
  };

  return (
    <div className={classNames(styles.component, className)}>
      {/* needed to equally space between elements */}
      <div />

      <div className={styles.sectionWrapper}>{renderSections()}</div>

      <div className={styles.buttonWrapper}>
        <Link onClick={handleClickClear}>
          <Typography className={styles.link} tagStyle="bodySmall">
            {t('items.common.words.clear')}
          </Typography>
        </Link>

        <div className={styles.buttonsRight}>
          {section?.type === SearchBarWrapperSectionType.PERSONS &&
            renderAddRoomButton(section)}

          <Button
            className={styles.button}
            onClick={handleClickButton}
            text={
              section?.type === SearchBarWrapperSectionType.DATE
                ? t('items.common.words.search')
                : t('items.common.words.next')
            }
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
};

SearchBarWidgetDropdown.displayName = 'SearchBarWidgetDropdown';

export default SearchBarWidgetDropdown;
